// The domain space public URLs live under, plus the tenant label to combine
// with the space slug when both share one DNS label. Single-tenant stacks use
// PUBLIC_WILDCARD_DOMAIN as-is — subdomain isolation is whatever the operator
// configured. A selfHostedSingleTenant stack composes the tenant's domain from
// STACK_PUBLIC_BASE_DOMAIN and the tenant's public_domain label, so its
// per-tenant domains never depend on the seeded tenant's env values. The
// tenant label is dot-joined (`<slug>.<tenant>.<base>`) unless the stack sets
// PUBLIC_COMBINED_SPACE_TENANT_HOSTS, whose TLS wildcard covers only one label
// so the tenant fuses onto the slug with a double dash
// (`<slug>--<tenant>.<base>`). With no base domain (localhost in
// development) there is no isolation and the caller falls back to the bare
// public host; a base with no tenant label is a broken stack, not a
// fallback.

export type PublicDomain = {
  wildcardDomain: string | undefined;
  combinedSpaceTenantLabel?: string;
};

export function derivePublicDomain(
  publicWildcardDomain: string | undefined,
  stackPublicBaseDomain: string | undefined,
  selfHostedSingleTenant: boolean,
  combinedSpaceTenantHosts: boolean,
  tenantPublicDomain: string | undefined
): PublicDomain {
  if (selfHostedSingleTenant) {
    return { wildcardDomain: publicWildcardDomain };
  }
  if (!stackPublicBaseDomain) {
    return { wildcardDomain: undefined };
  }
  if (!tenantPublicDomain) {
    throw new Error("derivePublicDomain: no tenant label on a SaaS stack");
  }
  if (combinedSpaceTenantHosts) {
    return {
      wildcardDomain: stackPublicBaseDomain,
      combinedSpaceTenantLabel: tenantPublicDomain,
    };
  }
  return {
    wildcardDomain: `${tenantPublicDomain}.${stackPublicBaseDomain}`,
  };
}
